// Calculated fields: reusable per-row numbers built from number columns, typed numbers,
// "time between two date columns" and text read as a number (see CalculatedField in types.ts).
// Fields whose result is TEXT are built in ./textCalculatedFields.ts and dispatched from here.
//
// Used two ways:
//   - Full Row Detail: each selected field is an extra column aliased "detail:calc:<id>",
//     which the Excel layout binds to exactly as it binds "detail:ruleSet:<id>".
//   - Count / Percentage: section.measure aggregates a field (SUM / AVG / MIN / MAX) in place
//     of COUNT(*). Percentage supports SUM only (a share of an average means nothing).
//
// SQL rules - mirrored by ReportSqlBuilder.CalculatedFields.cs, keep the two identical:
//   - Every operand is DECIMAL(38, 6) before any operator touches it. That avoids integer
//     division (7 / 2 = 3), int * int overflow, and "%" being invalid on float. Columns use
//     TRY_CONVERT so a text-backed number column yields blank for a bad value instead of
//     failing the whole query.
//   - The chain is parsed with standard precedence (x / mod before + -) into a tree and
//     every binary node is parenthesised, so the SQL reads exactly as the "Reads as" line.
//   - Divide / mod wrap the RIGHT operand in NULLIF(..., 0); "show 0" uses a CASE so only a
//     zero divisor becomes 0 - a blank operand still gives blank.
//   - Column operands are resolved through the catalog on the field's own model and must
//     pass isCalcNumberColumn / isCalcDateColumn; a number must match NUMBER_PATTERN. An
//     operand that fails either makes the whole field emit nothing (null).
import type {
    CalcOperand,
    CalcOperator,
    CalcTerm,
    CalculatedField,
    CalculatedValueType,
    DataColumn,
    MetricMeasureAggregate,
    MetricSection,
    ModelKey,
    ReportConfig,
} from "../types";
import type { ReportLogicContext } from "./context";
import { allColumnsForModel, columnMatchesKey, isCalculatedColumn } from "./catalogResolution";
import {
    datePartFieldEnglish,
    datePartFieldIssues,
    datePartFieldSql,
    datePartInputColumns,
    datePartValueType,
    isDatePartCalculatedField,
    simulateDatePartField,
} from "./dateParts";
import { columnDataTypeCategory } from "./operatorCatalog";
import { minutesBetweenSql } from "./sqlExpressions";
import { availableDifferenceUnits, isTemporalColumn, resolveDifferenceUnit, temporalPairIsComparable } from "./temporalPrecision";
import type { TimeDifferenceUnit } from "./temporalPrecision";
import {
    isTextOperand,
    simulateTextField,
    simulateTextOperand,
    textFieldEnglish,
    textFieldInputColumns,
    textFieldIssues,
    textFieldSql,
    textOperandInputColumn,
    textOperandIssue,
    textOperandSql,
    textOperandText,
} from "./textCalculatedFields";

export const CALC_DECIMAL = "DECIMAL(38, 6)";
export const MAX_CALC_TERMS = 6;
export const CALC_FIELD_NAME_MAX_LENGTH = 128;
export const MAX_CALC_ROUND_DIGITS = 6;
export const DETAIL_CALC_ALIAS_PREFIX = "detail:calc:";
// Up to 18 integer digits and 6 decimals: fits DECIMAL(38, 6) with room for the result.
const NUMBER_PATTERN = /^-?\d{1,18}(\.\d{1,6})?$/;
const LOOKUP_CODE_NUMERIC_TYPES = ["int", "bigint", "smallint", "tinyint", "decimal", "numeric"];

export const CALC_OPERATORS: { key: CalcOperator; symbol: string; label: string; sql: string; multiplicative: boolean }[] = [
    { key: "add", symbol: "+", label: "+ Add", sql: "+", multiplicative: false },
    { key: "subtract", symbol: "−", label: "− Subtract", sql: "-", multiplicative: false },
    { key: "multiply", symbol: "×", label: "× Multiply", sql: "*", multiplicative: true },
    { key: "divide", symbol: "÷", label: "÷ Divide", sql: "/", multiplicative: true },
    { key: "mod", symbol: "mod", label: "mod (Remainder)", sql: "%", multiplicative: true },
];

export const MEASURE_AGGREGATES: { key: MetricMeasureAggregate; label: string; sql: string }[] = [
    { key: "sum", label: "Sum of", sql: "SUM" },
    { key: "avg", label: "Average of", sql: "AVG" },
    { key: "min", label: "Minimum of", sql: "MIN" },
    { key: "max", label: "Maximum of", sql: "MAX" },
];

function operatorDef(operator?: CalcOperator) {
    return CALC_OPERATORS.find((item) => item.key === operator) || null;
}

// --- Field lookup ---------------------------------------------------------------------

export function calculatedFields(report?: ReportConfig | null) {
    return report?.calculatedFields || [];
}

export function findCalculatedField(report: ReportConfig | null | undefined, fieldId: string | undefined | null) {
    return fieldId ? calculatedFields(report).find((field) => field.id === fieldId) || null : null;
}

/** Fields usable by a metric: same data model only. */
export function calculatedFieldsForModel(report: ReportConfig | null | undefined, modelKey: ModelKey | "") {
    return modelKey ? calculatedFields(report).filter((field) => field.model === modelKey) : [];
}

/** True for a field that produces text from text pieces; see logic/textCalculatedFields.ts. */
export function isTextCalculatedField(field: CalculatedField | null | undefined) {
    return field?.resultType === "text";
}

export { isDatePartCalculatedField };

/** True for a field built from a formula (number result) - the only kind a measure can add up. */
export function isFormulaCalculatedField(field: CalculatedField | null | undefined) {
    return !!field && !isTextCalculatedField(field) && !isDatePartCalculatedField(field);
}

/** What the field evaluates to: drives the operators, value pickers and Excel format of its column. */
export function calculatedFieldValueType(field: CalculatedField | null | undefined): CalculatedValueType {
    if (!field || isTextCalculatedField(field)) return "text";
    if (isDatePartCalculatedField(field)) return datePartValueType(field);
    return "number";
}

/** Fields a Count / Percentage measure can add up: same data model, formula (number) results only. */
export function numberCalculatedFieldsForModel(report: ReportConfig | null | undefined, modelKey: ModelKey | "") {
    return calculatedFieldsForModel(report, modelKey).filter(isFormulaCalculatedField);
}

// --- Operand eligibility --------------------------------------------------------------

/**
 * A column a formula may use as a number: numeric type, not sensitive, and not a numeric
 * lookup code (an id whose meaning lives in a lookup - adding ids up is meaningless).
 * Mirrors IsCalcNumberColumn in ReportSqlBuilder.CalculatedFields.cs.
 */
export function isCalcNumberColumn(column?: DataColumn | null) {
    // A calculated field is never an input of another one (logic/calculatedColumns.ts).
    if (!column || column.isSensitive === true || isCalculatedColumn(column)) return false;
    if (columnDataTypeCategory(column) !== "numeric") return false;
    const dataType = String(column.dataType || "").trim().toLowerCase().replace(/\(.*$/, "");
    return !(column.lookupSourceId && LOOKUP_CODE_NUMERIC_TYPES.includes(dataType));
}

/** A date / time column a "time between" operand may use. Mirrors IsCalcDateColumn. */
export function isCalcDateColumn(column?: DataColumn | null) {
    return !!column && column.isSensitive !== true && !isCalculatedColumn(column) && isTemporalColumn(column);
}

export function calcNumberColumns(context: ReportLogicContext, modelKey: ModelKey | "") {
    return modelKey ? allColumnsForModel(context, modelKey).filter(isCalcNumberColumn) : [];
}

export function calcDateColumns(context: ReportLogicContext, modelKey: ModelKey | "") {
    return modelKey ? allColumnsForModel(context, modelKey).filter(isCalcDateColumn) : [];
}

function resolveFrom(columns: DataColumn[], key: string | undefined) {
    return key ? columns.find((column) => columnMatchesKey(column, key)) || null : null;
}

export function isValidCalcNumber(value: string | undefined | null) {
    return NUMBER_PATTERN.test(String(value ?? "").trim());
}

// --- Parsing the flat chain into a tree ---------------------------------------------

export type CalcTree =
    | { kind: "leaf"; operand: CalcOperand }
    | { kind: "op"; operator: CalcOperator; left: CalcTree; right: CalcTree };

/**
 * Standard precedence: multiplicative runs first, then the additive chain, both
 * left-associative - the same order SQL Server applies to the same chain. Null when the
 * chain is empty or a non-first term has no operator.
 */
export function parseCalcTerms(terms: CalcTerm[] | null | undefined): CalcTree | null {
    const list = terms || [];
    if (!list.length) return null;
    const additive: { operator?: CalcOperator; node: CalcTree }[] = [];
    let run: CalcTree = { kind: "leaf", operand: list[0].operand };
    let runOperator: CalcOperator | undefined;
    for (let index = 1; index < list.length; index++) {
        const term = list[index];
        const def = operatorDef(term.operator);
        if (!def) return null;
        const leaf: CalcTree = { kind: "leaf", operand: term.operand };
        if (def.multiplicative) {
            run = { kind: "op", operator: def.key, left: run, right: leaf };
        } else {
            additive.push({ operator: runOperator, node: run });
            run = leaf;
            runOperator = def.key;
        }
    }
    additive.push({ operator: runOperator, node: run });
    return additive.slice(1).reduce<CalcTree>(
        (left, item) => ({ kind: "op", operator: item.operator!, left, right: item.node }),
        additive[0].node,
    );
}

// --- SQL ------------------------------------------------------------------------------

function operandSql(context: ReportLogicContext, field: CalculatedField, operand: CalcOperand): string | null {
    const zero = field.blankAs === "zero";
    if (isTextOperand(operand)) return textOperandSql(context, field, operand);
    if (operand.kind === "number") {
        const value = String(operand.value ?? "").trim();
        return isValidCalcNumber(value) ? `CONVERT(${CALC_DECIMAL}, ${value})` : null;
    }
    if (operand.kind === "column") {
        const column = resolveFrom(calcNumberColumns(context, field.model), operand.column);
        if (!column) return null;
        const sql = `TRY_CONVERT(${CALC_DECIMAL}, ${column.sql})`;
        return zero ? `ISNULL(${sql}, 0)` : sql;
    }
    if (operand.kind === "minutes_between") {
        const dates = calcDateColumns(context, field.model);
        const from = resolveFrom(dates, operand.fromColumn);
        const to = resolveFrom(dates, operand.toColumn);
        if (!from || !to || !temporalPairIsComparable(from, to)) return null;
        const sql = `CONVERT(${CALC_DECIMAL}, ${minutesBetweenSql(from, to, operand.absolute, operand.differenceUnit as TimeDifferenceUnit | undefined)})`;
        return zero ? `ISNULL(${sql}, 0)` : sql;
    }
    return null;
}

function treeSql(node: CalcTree, leafSql: (operand: CalcOperand) => string | null, divideByZero: "blank" | "zero"): string | null {
    if (node.kind === "leaf") return leafSql(node.operand);
    const left = treeSql(node.left, leafSql, divideByZero);
    const right = treeSql(node.right, leafSql, divideByZero);
    if (!left || !right) return null;
    const def = operatorDef(node.operator)!;
    if (node.operator === "divide" || node.operator === "mod") {
        const safe = `${left} ${def.sql} NULLIF(${right}, 0)`;
        return divideByZero === "zero" ? `CASE WHEN ${right} = 0 THEN 0 ELSE ${safe} END` : `(${safe})`;
    }
    return `(${left} ${def.sql} ${right})`;
}

function roundDigits(field: CalculatedField) {
    const digits = field.roundTo;
    return typeof digits === "number" && Number.isInteger(digits) && digits >= 0 && digits <= MAX_CALC_ROUND_DIGITS ? digits : null;
}

/** The field's SQL expression, or null when any part of it is incomplete or not allowed. */
export function calculatedFieldSql(context: ReportLogicContext, report: ReportConfig, field: CalculatedField | null | undefined): string | null {
    if (!field || !field.model || !report.models.includes(field.model)) return null;
    // Text fields have their own shape and no rounding (logic/textCalculatedFields.ts).
    if (isTextCalculatedField(field)) return textFieldSql(context, field);
    // Date-part fields have their own shape and no rounding (logic/dateParts.ts).
    if (isDatePartCalculatedField(field)) return datePartFieldSql(context, field);
    if (!field.terms?.length || field.terms.length > MAX_CALC_TERMS) return null;
    const tree = parseCalcTerms(field.terms);
    if (!tree) return null;
    const sql = treeSql(tree, (operand) => operandSql(context, field, operand), field.divideByZero === "zero" ? "zero" : "blank");
    if (!sql) return null;
    const digits = roundDigits(field);
    return digits === null ? sql : `ROUND(${sql}, ${digits})`;
}

// --- Plain English --------------------------------------------------------------------

function operandText(context: ReportLogicContext, field: CalculatedField, operand: CalcOperand) {
    if (isTextOperand(operand)) return textOperandText(context, field, operand);
    if (operand.kind === "number") return String(operand.value ?? "").trim() || "?";
    if (operand.kind === "column") {
        return resolveFrom(calcNumberColumns(context, field.model), operand.column)?.label || "?";
    }
    const dates = calcDateColumns(context, field.model);
    const from = resolveFrom(dates, operand.fromColumn);
    const to = resolveFrom(dates, operand.toColumn);
    const unit = resolveDifferenceUnit(from, to, operand.differenceUnit as TimeDifferenceUnit | undefined);
    return `${operand.absolute ? "absolute " : ""}${unit}s between ${from?.label || "?"} and ${to?.label || "?"}`;
}

function treeText(node: CalcTree, leafText: (operand: CalcOperand) => string, nested: boolean): string {
    if (node.kind === "leaf") return leafText(node.operand);
    const text = `${treeText(node.left, leafText, true)} ${operatorDef(node.operator)!.symbol} ${treeText(node.right, leafText, true)}`;
    return nested ? `(${text})` : text;
}

/** e.g. "Billed Amount ÷ Trip Miles, rounded to 2 decimals". */
export function calculatedFieldEnglish(context: ReportLogicContext, field: CalculatedField | null | undefined) {
    if (!field) return "";
    if (isTextCalculatedField(field)) return textFieldEnglish(context, field);
    if (isDatePartCalculatedField(field)) return datePartFieldEnglish(context, field);
    const tree = parseCalcTerms(field.terms);
    if (!tree) return "";
    const parts = [treeText(tree, (operand) => operandText(context, field, operand), false)];
    const digits = roundDigits(field);
    if (digits !== null) parts.push(`rounded to ${digits} ${digits === 1 ? "decimal" : "decimals"}`);
    if (field.divideByZero === "zero" && field.terms.some((term) => term.operator === "divide" || term.operator === "mod")) parts.push("0 when dividing by zero");
    if (field.blankAs === "zero") parts.push("blank values count as 0");
    return parts.join(", ");
}

// --- Validation -----------------------------------------------------------------------

/** Every reason the field cannot produce SQL (empty when it is complete). */
export function calculatedFieldIssues(context: ReportLogicContext, report: ReportConfig, field: CalculatedField): string[] {
    const issues: string[] = [];
    const name = (field.name || "").trim();
    if (!name) issues.push("Give this calculated field a name.");
    else if (name.length > CALC_FIELD_NAME_MAX_LENGTH) issues.push(`The name must be ${CALC_FIELD_NAME_MAX_LENGTH} characters or fewer.`);
    else if (calculatedFields(report).some((other) => other.id !== field.id && (other.name || "").trim().toLowerCase() === name.toLowerCase())) {
        issues.push(`Another calculated field is already named "${name}".`);
    }
    if (!field.model) issues.push("Choose the data model this field is calculated on.");
    else if (!report.models.includes(field.model)) issues.push("This field's data model is no longer attached to the report.");
    if (isTextCalculatedField(field)) return issues.concat(textFieldIssues(context, field));
    if (isDatePartCalculatedField(field)) return issues.concat(datePartFieldIssues(context, field));
    if (!field.terms?.length) issues.push("Add at least one value to the formula.");
    if ((field.terms?.length || 0) > MAX_CALC_TERMS) issues.push(`A formula can have at most ${MAX_CALC_TERMS} values.`);
    if (field.roundTo !== undefined && field.roundTo !== null && roundDigits(field) === null) issues.push(`Rounding must be between 0 and ${MAX_CALC_ROUND_DIGITS} decimals.`);
    if (!field.model) return issues;

    const numbers = calcNumberColumns(context, field.model);
    const dates = calcDateColumns(context, field.model);
    (field.terms || []).forEach((term, index) => {
        const label = `Value ${index + 1}`;
        if (index > 0 && !operatorDef(term.operator)) issues.push(`${label}: choose an operation.`);
        const operand = term.operand;
        if (operand.kind === "number") {
            if (!isValidCalcNumber(operand.value)) issues.push(`${label}: enter a number (up to 18 digits and 6 decimals).`);
            else if ((term.operator === "divide" || term.operator === "mod") && Number(operand.value) === 0) issues.push(`${label}: dividing by 0 never gives a useful result - use another number.`);
        } else if (operand.kind === "column") {
            if (!operand.column) issues.push(`${label}: choose a number column.`);
            else if (!resolveFrom(numbers, operand.column)) issues.push(`${label}: that column is not a usable number column on this data model.`);
        } else if (isTextOperand(operand)) {
            const issue = textOperandIssue(context, field, operand);
            if (issue) issues.push(`${label}: ${issue}`);
        } else if (operand.kind === "minutes_between") {
            const from = resolveFrom(dates, operand.fromColumn);
            const to = resolveFrom(dates, operand.toColumn);
            if (!operand.fromColumn || !operand.toColumn) issues.push(`${label}: choose both date columns.`);
            else if (!from || !to) issues.push(`${label}: a date column is not usable on this data model.`);
            else if (!temporalPairIsComparable(from, to)) issues.push(`${label}: these two date columns can't be compared.`);
        } else {
            issues.push(`${label}: choose what this value is.`);
        }
    });
    return issues;
}

export function isCalculatedFieldReady(context: ReportLogicContext, report: ReportConfig, field: CalculatedField | null | undefined) {
    return !!field && !calculatedFieldIssues(context, report, field).length && !!calculatedFieldSql(context, report, field);
}

/** Units a "time between" operand may be measured in, for the editor. */
export function calcDifferenceUnits(context: ReportLogicContext, field: CalculatedField, operand: CalcOperand) {
    if (operand.kind !== "minutes_between") return [] as TimeDifferenceUnit[];
    const dates = calcDateColumns(context, field.model);
    return availableDifferenceUnits(resolveFrom(dates, operand.fromColumn), resolveFrom(dates, operand.toColumn));
}

// --- Rule simulator style "try it" ------------------------------------------------------

/**
 * Evaluates the formula on sample values (keyed by column key) with the same semantics as
 * the SQL: blank operand -> blank (or 0), zero divisor -> blank (or 0), then rounding.
 * Uses JavaScript numbers, so it is a preview, not a decimal-exact replica.
 */
export function simulateCalculatedField(context: ReportLogicContext, field: CalculatedField, inputs: Record<string, string>): string {
    if (isTextCalculatedField(field)) return simulateTextField(context, field, inputs);
    if (isDatePartCalculatedField(field)) return simulateDatePartField(context, field, inputs);
    const tree = parseCalcTerms(field.terms);
    if (!tree) return "";
    const zero = field.blankAs === "zero";
    const leaf = (operand: CalcOperand): number | null => {
        if (operand.kind === "number") return isValidCalcNumber(operand.value) ? Number(operand.value) : null;
        if (isTextOperand(operand)) {
            const value = simulateTextOperand(context, field, operand, inputs);
            return value === null && zero ? 0 : value;
        }
        if (operand.kind === "column") {
            const column = resolveFrom(calcNumberColumns(context, field.model), operand.column);
            const raw = column ? String(inputs[column.key] ?? "").trim() : "";
            const value = raw === "" || Number.isNaN(Number(raw)) ? null : Number(raw);
            return value === null && zero ? 0 : value;
        }
        const dates = calcDateColumns(context, field.model);
        const from = resolveFrom(dates, operand.fromColumn);
        const to = resolveFrom(dates, operand.toColumn);
        const start = from ? Date.parse(String(inputs[from.key] ?? "").trim().replace(" ", "T")) : NaN;
        const end = to ? Date.parse(String(inputs[to.key] ?? "").trim().replace(" ", "T")) : NaN;
        if (Number.isNaN(start) || Number.isNaN(end)) return zero ? 0 : null;
        const unit = resolveDifferenceUnit(from, to, operand.differenceUnit as TimeDifferenceUnit | undefined);
        const size = unit === "day" ? 86400000 : unit === "hour" ? 3600000 : 60000;
        const diff = Math.floor(end / size) - Math.floor(start / size);
        return operand.absolute ? Math.abs(diff) : diff;
    };
    const evaluate = (node: CalcTree): number | null => {
        if (node.kind === "leaf") return leaf(node.operand);
        const left = evaluate(node.left);
        const right = evaluate(node.right);
        if ((node.operator === "divide" || node.operator === "mod") && right === 0) return field.divideByZero === "zero" ? 0 : null;
        if (left === null || right === null) return null;
        switch (node.operator) {
            case "add": return left + right;
            case "subtract": return left - right;
            case "multiply": return left * right;
            case "divide": return left / right;
            case "mod": return left % right;
            default: return null;
        }
    };
    const result = evaluate(tree);
    if (result === null || !Number.isFinite(result)) return "(blank)";
    const digits = roundDigits(field);
    return digits === null ? String(Number(result.toFixed(6))) : result.toFixed(digits);
}

/** The column keys a "try it" panel needs a sample for, in formula order. */
export function calculatedFieldInputColumns(context: ReportLogicContext, field: CalculatedField): DataColumn[] {
    if (isDatePartCalculatedField(field)) return datePartInputColumns(context, field);
    if (isTextCalculatedField(field)) {
        const seenText = new Set<string>();
        return textFieldInputColumns(context, field).filter((column) => !seenText.has(column.key) && !!seenText.add(column.key));
    }
    const numbers = calcNumberColumns(context, field.model);
    const dates = calcDateColumns(context, field.model);
    const seen = new Set<string>();
    const result: DataColumn[] = [];
    const add = (column: DataColumn | null) => {
        if (!column || seen.has(column.key)) return;
        seen.add(column.key);
        result.push(column);
    };
    (field.terms || []).forEach((term) => {
        if (term.operand.kind === "column") add(resolveFrom(numbers, term.operand.column));
        if (isTextOperand(term.operand)) add(textOperandInputColumn(context, field, term.operand));
        if (term.operand.kind === "minutes_between") {
            add(resolveFrom(dates, term.operand.fromColumn));
            add(resolveFrom(dates, term.operand.toColumn));
        }
    });
    return result;
}

// --- Metric usage ---------------------------------------------------------------------

export function detailCalculatedFieldIds(section: MetricSection) {
    return section.detailCalculatedFieldIds || [];
}

export function detailCalcAlias(fieldId: string) {
    return `${DETAIL_CALC_ALIAS_PREFIX}${fieldId}`;
}

export function isDetailCalcAlias(alias: string) {
    return alias.startsWith(DETAIL_CALC_ALIAS_PREFIX);
}

export interface DetailCalculatedColumn {
    fieldId: string;
    field: CalculatedField | null;
    alias: string;
    label: string;
    expr: string | null;
}

/** The calculated columns a Full Row Detail metric adds, in its chosen order. */
export function detailCalculatedColumns(context: ReportLogicContext, report: ReportConfig | null | undefined, section: MetricSection): DetailCalculatedColumn[] {
    if (!report || section.displayAs !== "detail") return [];
    return detailCalculatedFieldIds(section).map((fieldId) => {
        const field = findCalculatedField(report, fieldId);
        const usable = field && field.model === section.model ? field : null;
        return {
            fieldId,
            field,
            alias: detailCalcAlias(fieldId),
            label: (field?.name || "").trim() || "Calculated Field",
            expr: usable ? calculatedFieldSql(context, report, usable) : null,
        };
    });
}

/** True when the metric aggregates a calculated field instead of counting rows. */
export function usesMeasureField(section: MetricSection) {
    return (section.displayAs === "count" || section.displayAs === "percentage") && !!section.measure?.fieldId;
}

export function measureAggregateDef(section: MetricSection) {
    return MEASURE_AGGREGATES.find((item) => item.key === section.measure?.aggregate) || MEASURE_AGGREGATES[0];
}

/**
 * The aggregate replacing COUNT(*), e.g. "SUM(<field sql>)". Null when the metric counts
 * rows, or when its measure cannot be built (unknown field, other model, incomplete
 * formula, or a non-SUM aggregate on a percentage metric).
 */
export function measureAggregateSql(context: ReportLogicContext, report: ReportConfig, section: MetricSection): string | null {
    if (!usesMeasureField(section)) return null;
    const def = measureAggregateDef(section);
    if (section.displayAs === "percentage" && def.key !== "sum") return null;
    const field = findCalculatedField(report, section.measure!.fieldId);
    if (!field || field.model !== section.model || !isFormulaCalculatedField(field)) return null;
    const sql = calculatedFieldSql(context, report, field);
    return sql ? `${def.sql}(${sql})` : null;
}

/** e.g. "Sum of Cost Per Mile"; "Count" when the metric counts rows. */
export function measureLabel(report: ReportConfig | null | undefined, section: MetricSection) {
    if (!usesMeasureField(section)) return "Count";
    const field = findCalculatedField(report, section.measure!.fieldId);
    return `${measureAggregateDef(section).label} ${(field?.name || "").trim() || "Calculated Field"}`;
}

/** Why the metric's measure cannot be built, or null. */
export function measureIssue(context: ReportLogicContext, report: ReportConfig, section: MetricSection): string | null {
    if (!section.measure || (section.displayAs !== "count" && section.displayAs !== "percentage")) return null;
    if (!section.measure.fieldId) return "Choose the calculated field this metric adds up.";
    const field = findCalculatedField(report, section.measure.fieldId);
    if (!field) return "The calculated field this metric uses no longer exists. Choose another one.";
    if (field.model !== section.model) return `"${field.name}" is calculated on a different data model than this metric.`;
    if (isTextCalculatedField(field)) return `"${field.name || "The calculated field"}" is a text field and can't be added up. Choose a number field, or add it as a column to a Full Row Detail metric.`;
    if (isDatePartCalculatedField(field)) return `"${field.name || "The calculated field"}" is a date part and can't be added up. Break the metric down by it instead, or choose a number field.`;
    if (section.displayAs === "percentage" && measureAggregateDef(section).key !== "sum") return "A percentage metric can only use \"Sum of\" a calculated field.";
    if (!calculatedFieldSql(context, report, field)) return `"${field.name || "The calculated field"}" is not complete yet. Finish it on the Calculated Fields tab.`;
    return null;
}
